import { useMemo } from "react";

import {
  FormattedCellKey,
  RowHeader,
  ColumnHeader,
  BaseCellData,
} from "@/pages/EditSubjectPlan/types/types";

import { getFormattedCellKey } from "@/utils/getFormattedCellKey/getFormattedCellKey";

type RestrictedCellBoundary = {
  left: boolean;
  right: boolean;
};

export type CellBoundaries = {
  [key: FormattedCellKey]: RestrictedCellBoundary;
};

type UseCellBoundariesProps = {
  /**
   * The rows of the table in the order they will render
   */
  orderedRows: RowHeader[];

  /**
   * The columns of the table in the order they will render
   */
  orderedColumns: ColumnHeader[];

  /**
   * The cells of the table. These can be in any order
   */
  cells: BaseCellData[];
};

/**
 * Determine the required boundaries of a span control so it does not move into occupied cells.
 * This hook does not stop the span control from moving into empty cells, but it does give you
 * the data to determine if it should.
 *
 * NOTE: I'd like each update to be managed by the cell updates. I think this will be more performant. So when a cell is updated, it will do a lighter lookaround, and then update the boundaries on both the old location and the new location.
 */
export const useCellBoundaries = ({
  cells,
  orderedColumns,
  orderedRows,
}: UseCellBoundariesProps) => {
  const cellBoundaries: CellBoundaries = useMemo(() => {
    const boundaries: CellBoundaries = {};

    if (orderedColumns.length === 0) {
      return boundaries;
    }

    // foreach of the row/column combinations, we need to check if a cell is occupied or not
    orderedRows.forEach((row) => {
      // keep this here so it resets on each row
      let spanedCellsToSkip = 0;

      orderedColumns.forEach((column, index) => {
        const formattedCellKey: FormattedCellKey = getFormattedCellKey({
          row: row.id,
          column: column.id,
        });

        // if this cell is occupiued by a spanned cell, skip it and move on
        if (spanedCellsToSkip > 0) {
          spanedCellsToSkip--;
          return;
        }

        const cell = cells.find(
          (cell) => cell.row === row.id && cell.column === column.id
        );

        if (cell) {
          // If cell is being spanned, then we need to set how many additional cells we need to skip
          if (cell?.span && cell.span > 1) {
            spanedCellsToSkip = cell.span - 1;

            // if the cell is spanned across the entire table, then we need to add the right boundary to the current cell
            if (index + cell?.span === orderedColumns.length) {
              boundaries[formattedCellKey] = {
                ...boundaries[formattedCellKey],
                right: true,
              };
            }

            // Adds the left boundary to the next empty cell when the current cell is spanned
            const nextColumn = orderedColumns[index + cell.span];
            if (nextColumn) {
              const nextFormattedCellKey: FormattedCellKey =
                getFormattedCellKey({
                  row: row.id,
                  column: nextColumn.id,
                });

              boundaries[nextFormattedCellKey] = {
                ...boundaries[nextFormattedCellKey],
                left: true,
              };
            }
          }
        }

        // if the left boundary is not set then we need to check

        if (boundaries[formattedCellKey]?.left === undefined) {
          // Adds the left boundary to the current cell
          const previousColumn = orderedColumns[index - 1];

          // this will be skipped if we are at the start of the row
          if (previousColumn) {
            const previousCell = cells.find(
              (cell) => cell.row === row.id && cell.column === previousColumn.id
            );

            // if there is a previous column, then we need to check if it is occupied
            if (previousCell) {
              // if the previous cell is occupied then we need to set the left boundary to true
              boundaries[formattedCellKey] = {
                ...boundaries[formattedCellKey],
                left: true,
              };
            }
          }
        }

        if (boundaries[formattedCellKey]?.right === undefined) {
          // Adds the right boundary to the current cell
          const lookahead = cell?.span ? index + cell.span : index + 1;
          const nextColumn = orderedColumns[lookahead];

          // this will be skipped if we are at the end of the row
          if (nextColumn) {
            const nextCell = cells.find(
              (cell) => cell.row === row.id && cell.column === nextColumn.id
            );

            // if there is a next column, then we need to check if it is occupied
            if (nextCell) {
              // if the next cell is occupied then we need to set the right boundary to true
              boundaries[formattedCellKey] = {
                ...boundaries[formattedCellKey],
                right: true,
              };
            }
          }
        }
      });

      // Adds the left and right boundaries to the first and last cells of each row
      const firstColumn = orderedColumns[0];
      const lastColumn = orderedColumns[orderedColumns.length - 1];

      const firstFormattedCellKey = getFormattedCellKey({
        row: row.id,
        column: firstColumn.id,
      });

      const lastFormattedCellKey = getFormattedCellKey({
        row: row.id,
        column: lastColumn.id,
      });

      boundaries[firstFormattedCellKey] = {
        ...boundaries[firstFormattedCellKey],
        left: true,
      };

      boundaries[lastFormattedCellKey] = {
        ...boundaries[lastFormattedCellKey],
        right: true,
      };
    });

    return boundaries;
  }, [orderedRows, orderedColumns, cells]);

  return { cellBoundaries };
};
